import Swiper from 'swiper';
import { Navigation, Pagination, Mousewheel } from 'swiper/modules'
import Tab from '@/scripts/optional/tab'
import { mousewheel } from '@/scripts/core/global'

export default class TabDOM {
	private isRestructured: boolean = false;
	private tabSections: NodeListOf<Element>;
	private swiperInstances: Map<HTMLElement, Swiper> = new Map();

	constructor() {
		this.tabSections = document.querySelectorAll('.tab__section');
		this.restructureTabSections();
	}

	private restructureTabSections(): void {
		try {
			this.tabSections.forEach((section: Element) => {
				const buttons = section.querySelectorAll('.tab__button');
				const bodies = section.querySelectorAll('.tab__body');

				if (buttons.length === 0 && bodies.length === 0) {
					return;
				}

				const navigation = document.createElement('div');
				navigation.className = 'tab__navigation swiper';

				const swiperWrapper = document.createElement('div');
				swiperWrapper.className = 'swiper-wrapper';

				buttons.forEach((button: Element) => {
					const swiperSlide = document.createElement('div');
					swiperSlide.className = 'swiper-slide';
					swiperSlide.appendChild(button);
					swiperWrapper.appendChild(swiperSlide);
				});

				navigation.appendChild(swiperWrapper);

				const content = document.createElement('div');
				content.className = 'tab__content';

				bodies.forEach((body: Element) => {
					content.appendChild(body);
				});

				section.appendChild(navigation);
				section.appendChild(content);
			});

			this.isRestructured = true;
			
			this.runExternalModules();
		} catch (error) {
			console.error('Error restructuring tab sections:', error);
			this.isRestructured = false;
		}
	}

	private runExternalModules(): void {
		if (!this.isRestructured) {
			console.warn('TabDOM: Cannot run external modules - restructuring not complete');
			return;
		}

		this.handleNavigation()
		this.handleTabSystem()
		this.handleInnerSlider();

	}

	private handleNavigation(): void {
		this.tabSections?.forEach((section:Element) => {
			const navigation = section.querySelector('.tab__navigation') as HTMLElement;

			new Swiper(navigation, {
				slidesPerView: 3,
				breakpoints: {
					1024: {
						spaceBetween: 24
					}
				}
			})
		})
	}

	private handleTabSystem():void {
		this.tabSections?.forEach((section:Element) => {
			new Tab({
				container: section as HTMLElement,
				tabButtons: '.tab__button',
				tabBody: '.tab__body',
				activeClass: 'active'
			});

			this.setupTabChangeListener(section as HTMLElement);
		})
	}

	private setupTabChangeListener(section: HTMLElement): void {
		const tabBodies = section.querySelectorAll('.tab__body');
		tabBodies.forEach((body: Element) => {
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
						const target = mutation.target as HTMLElement;
						if (target.classList.contains('active')) {
							// Tab body became active, update its inner slider
							this.updateInnerSliderForTabBody(target);
						}
					}
				});
			});

			observer.observe(body, {
				attributes: true,
				attributeFilter: ['class']
			});
		});
	}

	private updateInnerSliderForTabBody(tabBody: HTMLElement): void {
		const innerSlider = tabBody.querySelector('.tab__body-slider') as HTMLElement;
		if (innerSlider) {
			const swiperInstance = this.swiperInstances.get(innerSlider);
			if (swiperInstance) {
				swiperInstance.update();
				swiperInstance.updateSize();
				swiperInstance.updateSlides();
			}
		}
	}

	private handleInnerSlider(): void {
		this.tabSections.forEach((section: Element) => {
			const tabBodies = section.querySelectorAll('.tab__body');
			tabBodies.forEach((body: Element) => {
				const innerSlider = body.querySelector('.tab__body-slider') as HTMLElement;

				if (innerSlider) {
					const swiperInstance = new Swiper(innerSlider, {
						modules: [Navigation, Pagination, Mousewheel],
						slidesPerView: 1.39,
						mousewheel,
						navigation: {
							prevEl: innerSlider.querySelector(
								'.swiper-button-prev'
							) as HTMLButtonElement,
							nextEl: innerSlider.querySelector(
								'.swiper-button-next'
							) as HTMLButtonElement,
						},
						pagination: {
							el: innerSlider.querySelector(
								'.swiper-pagination'
							) as HTMLElement,
							clickable: true,
						},
						breakpoints: {
							990: {
								slidesPerView: 3,
							},
						},
					})

					this.swiperInstances.set(innerSlider, swiperInstance);
				}
			});
		})
	}

	public getIsRestructured(): boolean {
		return this.isRestructured;
	}
}